/**
 * Roles are now admin-defined (see Vai trò / admin/roles) — there is no
 * fixed role enum anymore. POST /auth/login returns the logged-in user's
 * *effective* permissions directly (`user.permissions`, one
 * {view,create,update,delete} entry per F-group module), computed
 * server-side from their role's role_permissions. `can()` just reads that
 * map; it never hardcodes what a role may do.
 *
 * Action letters are kept only because router guards / menuConfig already
 * call `can(perms, group, 'X'|'G'|'P'|'D')` everywhere — they mirror the
 * backend's RolePermission.Allows() mapping:
 *   X = view, G = create OR update, P = update, D = delete.
 */
export { can } from './permissionCore.js'

// --- Dev-only mockup support (see router's /preview/:role) ---------------
// No backend involved there, so we keep a small legacy spec matrix to
// synthesize a plausible permissions map for each of the 4 example roles.
const DEV_ROLE_MATRIX = {
  admin:   { F1: 'XP',   F2: 'X',    F3: 'X',   F4: 'XPD',  F5: 'X',   F6: 'X',   F7: 'XP',  F8: 'XGPD', F9: 'XP', F10: 'X',  F11: 'XP', F12: 'XG', F13: 'XGPD', F14: 'XP', F15: 'XGPD' },
  manager: { F1: 'XG',   F2: 'XGP',  F3: 'XG',  F4: 'XGP',  F5: 'X',   F6: 'XGP', F7: 'XG',  F9: 'X',    F10: 'XG', F11: 'X',  F12: 'XG', F13: 'X', F14: 'XG', F15: 'X' },
  vet:     { F1: 'XGP',  F3: 'XG',   F4: 'X',   F5: 'XGP',  F6: 'X',   F7: 'XGP', F11: 'X',  F12: 'X',   F13: 'X', F14: 'XGP', F15: 'X' },
  worker:  { F1: 'XG',   F4: 'XG',   F5: 'XG',  F6: 'XG',   F7: 'XG',  F11: 'XG', F12: 'XG', F13: 'X', F14: 'XG', F15: 'X' }
}

export function devPermissionsForRole(role) {
  const rules = DEV_ROLE_MATRIX[role] || {}
  const out = {}
  for (const [mod, actions] of Object.entries(rules)) {
    out[mod] = {
      view: actions.includes('X'),
      create: actions.includes('G'),
      update: actions.includes('G') || actions.includes('P'),
      delete: actions.includes('D')
    }
  }
  return out
}

// Dev-preview-only role label (see loginMock) — real users get `role_name`
// straight from the backend (admin-defined, not translatable app chrome).
import { i18n } from '@/i18n'

export function devRoleLabel(role) {
  const key = `preview.roleLabels.${role}`
  return i18n.global.te(key) ? i18n.global.t(key) : role
}
